import React, { FC } from 'react';
import { InputAdornment, inputAdornmentClasses, inputClasses, SxProps, TextField, TextFieldProps, Theme, TypographyVariants } from '@mui/material';

import { useInputOnChange } from '../../hooks';
import { Close24, Search24 } from '../../icons';


export interface SearchProps extends Omit<TextFieldProps, 'variant' | 'onChange' | 'value'> {
    size?: 'medium' | 'small';
    isGray?: boolean;
    onChange: (value: string) => void;
    value: string;
    inputPropsSx?: SxProps<Theme>;
    textFieldPropsSx?: SxProps<Theme>;
}

const typographyBySize: Record<SearchProps['size'], keyof TypographyVariants> = {
    medium: 'regularBody2',
    small: 'regularCaption1',
};

export const Search: FC<SearchProps> = ({ size = 'medium', isGray, sx, textFieldPropsSx, inputPropsSx, value, onChange, ...props }) => (
    <TextField
        variant='filled'
        onChange={useInputOnChange(onChange, false)}
        value={value}
        InputProps={{
            startAdornment: (
                <InputAdornment
                    position='start'
                    sx={({ palette: { grayscale } }) => ({
                        color: grayscale.shade85,

                        [`&.${inputAdornmentClasses.root}.${inputAdornmentClasses.positionStart}.${inputAdornmentClasses.root}:not(${inputAdornmentClasses.hiddenLabel})`]: {
                            mt: 0,
                            mr: 3,
                        },
                    })}
                >
                    <Search24
                        sx={({ typography }) => ({
                            fontSize: typography[typographyBySize[size] as string].lineHeight,
                        })}
                    />
                </InputAdornment>
            ),
            endAdornment: value && (
                <InputAdornment
                    position='end'
                    onClick={() => onChange('')}
                    sx={({ palette: { grayscale } }) => ({
                        color: grayscale.shade100,
                        cursor: 'pointer',
                    })}
                >
                    <Close24
                        sx={({ spacing }) => ({
                            fontSize: spacing(4),
                        })}
                    />
                </InputAdornment>
            ),
            sx: ({ typography, palette: { grayscale } }) => ({
                height: 'auto',
                py: 2,
                px: 4,
                backgroundColor: isGray ? grayscale.shade10 : grayscale.shade0,
                input: {
                    height: 'auto',
                    p: 0,
                    '&::placeholder': {
                        color: grayscale.shade85,
                        opacity: 1,
                    },
                },
                ...typography[typographyBySize[size] as string],
                ...inputPropsSx,
            }),
        }}
        {...props}
        sx={[({ palette }) => ({
            [`& div.${inputClasses.focused}`]: {
                border: 'none',
                backgroundColor: isGray ? palette.grayscale.shade10 : palette.grayscale.shade0,
            },
            ...textFieldPropsSx,
        }),
        ...Array.isArray(sx) ? sx : [sx],
        ]}
    />
);
